import 'react-app-polyfill/stable'
// Harus sebelum import yang memuat @/store (hash history dibuat saat import)
import 'utils/lazadaCallbackRedirect'
import 'utils/chunkReloadFallback'
import React, { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { QueryClient, QueryClientProvider } from 'react-query'
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import axios from 'axios'
import { GoogleOAuthProvider } from '@react-oauth/google'
import ConfigProvider from 'components/LayoutComponents/ConfigProvider'
import { generateBaseUri } from 'utils/helper'
import { errorHandler, requestHandler, startFocusRefresh, successHandler } from 'utils/axios'
import { getToken } from 'utils/cookies'
import { storedSettings } from 'utils/localStorage'
import { markDataChangedFromResponse } from 'utils/summaryCache'
import setupSentry from 'utils/sentry'
import { AppProvider } from 'components/AppProvider'
import { initializeFp, initializeTagManager, trackingPage } from 'utils/gtm'
import setupDayjs from 'utils/dayjs'
import { ClearBrowserCacheBoundary } from 'react-clear-browser-cache'
import { PersistGate } from 'redux-persist/integration/react'
import { history, store } from '@/store'
import App from '@/app'
import PersistLoading from '@/components/UI/PersistLoading'

// app styles
import './global.scss'

// React Scan (profiler dev) — aktif hanya bila VITE_USE_REACT_SCAN=true di .env.
// Dynamic import: react-scan tidak ikut ter-load saat flag mati (default).
if (import.meta.env.VITE_USE_REACT_SCAN === 'true') {
  import('react-scan').then(({ scan }) => {
    scan({ enabled: true })
  })
}

setupDayjs()
setupSentry()

if (import.meta.env.PROD) {
  // Initialize tracker
  initializeTagManager()
  initializeFp()
}

axios.defaults.baseURL = generateBaseUri()
axios.defaults.headers.Authorization = `Bearer ${getToken()}`
axios.defaults.headers.post['Content-Type'] = 'application/json'
axios.defaults.headers.common['X-App'] = 'finance'
axios.defaults.headers.common['X-Client-Version'] = import.meta.env.PACKAGE_VERSION
axios.defaults.timeout = 300000

axios.interceptors.request.use((request) => requestHandler(request))

axios.interceptors.response.use(
  (response) => {
    markDataChangedFromResponse(response)
    return successHandler(response)
  },
  (error) => errorHandler(error, store, history),
)

// Refresh token proaktif hanya saat tab Kledo fokus (tab background tidak refresh)
startFocusRefresh()

// Use query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
})

Chart.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Legend,
  Title,
  Tooltip,
)

const AppContainer = () => {
  // Ketika di production dipasang tracking
  useEffect(() => {
    if (import.meta.env.PROD) {
      history.listen((location) => {
        trackingPage(location.pathname)
      })
    }
  }, [])

  // useEffect(() => {
  //   store.persistor.purge()
  // }, [])

  return (
    <AppProvider
      value={{
        settings: storedSettings({
          isMobileMenuOpen: false,
          isMenuCollapsed: false,
        }),
      }}
    >
      <Provider store={store.store}>
        <PersistGate loading={<PersistLoading />} persistor={store.persistor}>
          <QueryClientProvider client={queryClient}>
            <ConfigProvider>
              <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
                <App history={history} />
              </GoogleOAuthProvider>
            </ConfigProvider>
          </QueryClientProvider>
        </PersistGate>
      </Provider>
    </AppProvider>
  )
}

const root = ReactDOM.createRoot(document.getElementById('root'))

root.render(
  // Dev dipercepat supaya simulasi deploy (ganti versi meta.json) tidak menunggu 5 menit
  <ClearBrowserCacheBoundary duration={import.meta.env.DEV ? 10000 : 300000}>
    <AppContainer />
  </ClearBrowserCacheBoundary>,
)

if (window.Cypress) {
  window.store = store
}
